import React, { memo } from 'react'
import tw from 'twin.macro'
import { DataRow } from 'src/components/shared/DataRow'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { RetryConfigPayment } from 'src/interfaces/retryConfig'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'

interface Props {
  payment: RetryConfigPayment | null
}

const MISSING_VALUE = '—'

// A missing value renders a dash rather than fabricated business content. A `null` on any of
// these fields (e.g. `failureReason`) is a real reading — it means there was no failure — not
// missing data, so it is never replaced with invented text.
const formatNullableText = (value: string | null): string => {
  return value ?? MISSING_VALUE
}

// Every payment date field arrives as a timestamp STRING (unlike `RetryConfig.createdAt`, which
// is unix seconds) — pair `parseFlexibleDateTime` with `formatDateTime`, the repo's existing
// grid-date utils, rather than an inline Luxon `fromISO` call.
const formatTimestamp = (value: string | null): string => {
  return formatDateTime(parseFlexibleDateTime(value))
}

const PaymentInfoCardComponent = ({ payment }: Props) => {
  // Render nothing rather than an empty shell when the backend gave us no payment at all.
  if (!payment) {
    return null
  }

  return (
    <SectionCard title="Payment info">
      <div css={tw`flex flex-col gap-2`}>
        <DataRow title="Date from" text={formatTimestamp(payment.dateFrom)} />
        <DataRow title="Date to" text={formatTimestamp(payment.dateTo)} />
        <DataRow title="Amount" text={formatCentsAsUsd(payment.amount)} />
        <DataRow title="Status" text={formatNullableText(payment.status)} />
        <DataRow
          title="Partner driver ID"
          text={formatNullableText(payment.partnerDriverId)}
          direction="column"
        />
        <DataRow
          title="Partner driver external ID"
          text={formatNullableText(payment.partnerDriverExternalId)}
          direction="column"
        />
        <DataRow title="Failure reason" text={formatNullableText(payment.failureReason)} />
        <DataRow title="Failure reason type" text={formatNullableText(payment.failureReasonType)} />
        <DataRow
          title="Internal failure reason"
          text={formatNullableText(payment.internalFailureReason)}
        />
        <DataRow title="Payout method" text={formatNullableText(payment.payoutMethod)} />
        <DataRow title="Destination type" text={formatNullableText(payment.destinationType)} />
        <DataRow title="Destination last 4" text={formatNullableText(payment.destinationLast4)} />
        <DataRow title="Uploaded at" text={formatTimestamp(payment.uploadedAt)} />
        <DataRow title="Paid at" text={formatTimestamp(payment.paidAt)} />
        <DataRow title="Processed at" text={formatTimestamp(payment.processedAt)} />
        <DataRow title="Status updated at" text={formatTimestamp(payment.statusUpdatedAt)} />
      </div>
    </SectionCard>
  )
}

export const PaymentInfoCard = memo(PaymentInfoCardComponent)
